// Filter Modal - Full-screen modal for filter options
// Similar to AdvancedSearchModal but for filtering

import { Modal, Icon, Dropdown, Button, List } from 'semantic-ui-react';
import Counter from './Counter';
import ResidueMenu from './ResidueMenu';
import SNFGsvg from '../visualizer-tool/glycan-interpreter/snfgSVG';
import databaseUtils from '../databaseUtils';
import '../css/filter-modal.css';

export default function FilterModal({
  open,
  onClose,
  // Filter states
  branched,
  setBranched,
  antigenType,
  setAntigenType,
  repeatedUnits,
  setRepeatedUnits,
  sortBy,
  setSortBy,
  residues,
  setResidues,
  // Actions
  handleFilterSubmit,
  resetFilters,
  // Data
  maxRepeatUnit,
  antigenTypes,
  applied,
  resetApplied,
  // Residue menu
  residueMenuOpen,
  setResidueMenuOpen
}) {
  // Map antigen types to dropdown options
  const antigenTypeOptions = [
    { key: 1, text: 'Default', value: '' },
    ...antigenTypes.map((type, index) => ({
      key: index + 2,
      text: type,
      value: type
    }))
  ];

  // Add residue to the array
  const addResidue = (residue) => {
    setResidues(databaseUtils.addResidue(residue, residues));
  };

  // Toggle residue menu open/close
  const toggleResidueMenu = () => {
    setResidueMenuOpen(!residueMenuOpen);
  };

  const handleApply = () => {
    handleFilterSubmit();
    onClose();
  };

  const handleReset = () => {
    resetFilters();
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="small"
      className="filter-modal"
    >
      <Modal.Header>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Icon name="filter" />
          Filter Options
        </div>
        <Icon
          name="close"
          link
          onClick={onClose}
          style={{ position: 'absolute', right: '1.5rem', top: '1.5rem', cursor: 'pointer' }}
        />
      </Modal.Header>

      <Modal.Content scrolling>
        <div className="filter-modal-content">
          {residueMenuOpen && (
            <div className="residue-menu-overlay">
              <ResidueMenu
                addResidue={addResidue}
                toggleResidueMenu={toggleResidueMenu}
              />
            </div>
          )}

          <div className="ui form">
            <div className="field">
              <label>Antigen Type:</label>
              <Dropdown
                value={antigenType}
                onChange={(e, { value }) => setAntigenType(value)}
                placeholder="Default"
                options={antigenTypeOptions}
                selection
                clearable
                fluid
              />
            </div>

            <div className="field">
              <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                Include Specific Residues:
                <Button
                  size="small"
                  icon={residueMenuOpen ? 'angle left' : 'angle right'}
                  onClick={toggleResidueMenu}
                  style={{ marginLeft: '8px' }}
                />
              </label>
              <List divided relaxed>
                {residues.map((residue, index) => (
                  <List.Item key={index} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <List.Icon onClick={() => addResidue(residue)}>
                      <SNFGsvg snfg={residue} />
                    </List.Icon>
                    <List.Content>{residue}</List.Content>
                    <List.Content style={{ marginLeft: 'auto' }}>
                      <Button
                        size="tiny"
                        icon="trash"
                        onClick={() => setResidues(databaseUtils.removeResidue(residue, residues))}
                      />
                    </List.Content>
                  </List.Item>
                ))}
              </List>
            </div>

            <div className="field">
              <Counter
                label="Repeated Unit Size:"
                repeatedUnits={repeatedUnits}
                setRepeatedUnits={setRepeatedUnits}
                max={maxRepeatUnit}
              />
            </div>

            <div className="field">
              <label>Branched?</label>
              <div style={{ display: 'flex', gap: '24px', marginTop: '8px' }}>
                <div className="ui radio checkbox">
                  <input
                    type="radio"
                    name="branched"
                    value="1"
                    checked={branched === '1'}
                    onChange={(e) => setBranched(e.target.value)}
                  />
                  <label>Yes</label>
                </div>
                <div className="ui radio checkbox">
                  <input
                    type="radio"
                    name="branched"
                    value="0"
                    checked={branched === '0'}
                    onChange={(e) => setBranched(e.target.value)}
                  />
                  <label>No</label>
                </div>
                <div className="ui radio checkbox">
                  <input
                    type="radio"
                    name="branched"
                    value=""
                    checked={branched === ''}
                    onChange={(e) => setBranched(e.target.value)}
                  />
                  <label>Either</label>
                </div>
              </div>
            </div>

            <div className="field">
              <label>Sort by Number of Sugar Units:</label>
              <Dropdown
                value={sortBy}
                onChange={(e, { value }) => setSortBy(value)}
                placeholder="Default"
                options={[
                  { key: 1, text: 'Ascending', value: 'asc' },
                  { key: 2, text: 'Descending', value: 'desc' }
                ]}
                selection
                clearable
                fluid
              />
            </div>
          </div>
        </div>
      </Modal.Content>

      <Modal.Actions>
        <div className="filter-modal-actions">
          <button
            className={applied ? 'filter-apply-btn active' : 'filter-apply-btn'}
            onClick={handleApply}
          >
            <Icon name="check" /> Apply Filters
          </button>
          <button
            className={resetApplied ? 'filter-reset-btn active' : 'filter-reset-btn'}
            onClick={handleReset}
          >
            <Icon name="redo" /> Reset Filters
          </button>
        </div>
      </Modal.Actions>
    </Modal>
  );
}
